import React, { useEffect, useCallback, useState } from "react";
import { graphql, withPrefix } from "gatsby";
import classNames from "classnames";
import { Link } from "./link";
import Logo from "../icons/logo.svg";
import ArrowRight from "../icons/arrow-right-black.svg";
import ArrowDown from "../icons/arrow-down.svg";
import FooterIcon from "./footer-icon";
import { normalizeRelativePath } from "../utility/paths";

const Pages = ({ item, region }) => {
  const isRussiaBox = item.some((el) => el?.russiaCode);
  let pages;

  if (isRussiaBox) {
    if (region && region !== "not available") {
      pages = item.filter(
        (el) => !el?.russiaCode || el?.russiaCode.split(",").some((code) => code === region)
      );
    } else {
      pages = item.filter(
        (el) => !el?.russiaCode || el?.russiaCode.split(",").some((code) => code === "VOR")
      );
    }
  } else {
    pages = item;
  }

  return pages.map((page, j) => (
    <li key={j}>
      {page.slug && page.slug ? (
        <Link to={normalizeRelativePath(page.slug)} className="footer__link">
          {page.liferayFields.title}
        </Link>
      ) : (
        <Link
          to={page.externalLink}
          target={page.target ? page.target?.[0] : ""}
          rel={page.target && page.target?.[0] === "_blank" ? "noopener noreferrer" : ""}
          className="footer__link"
        >
          {page.liferayFields.title}
        </Link>
      )}
    </li>
  ));
};

const Footer = ({ data, customFooter, generatedLocale }) => {
  const { footerMenuFiltered } = data;
  const footerMenu = customFooter ? customFooter : footerMenuFiltered;
  const { payAndSocialFooter, legalInfo, localeContent } = footerMenu;
  const columnName = footerMenu?.columnNameFieldSet?.reduce(
    (res, item) =>
      !!item?.page?.some((page) => !!page?.liferayFields?.title)
        ? res.concat({
            ...item,
            content: {
              ...item,
              page: item.page.filter((page) => !!page?.liferayFields?.title),
            },
          })
        : res,
    []
  );

  const [region, setRegion] = useState();

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await fetch(`/api-geo/`);
        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        setRegion(data["country-region"]);
      } catch (err) {
        console.error("Error calling api-geo: ", err);
      }
    };
    loadCountries();
  }, []);

  const [openMap, setOpenMap] = useState({});
  const toggleItem = useCallback((e) => {
    const clickedIdx = parseInt(e.target.name);
    setOpenMap((openMap) => ({ ...openMap, [clickedIdx]: !!openMap[clickedIdx] ? false : true }));
  }, []);

  return (
    <div className="footer">
      <div className="container-fluid wrapper">
        <div className="row">
          <div className="col-12 order-md-2">
            <div className="footer__border d-flex justify-content-between align-items-center py-3 py-md-4 mt-md-3">
              <div className="d-flex align-items-center pr-3">
                <div className="logo">
                  <Link className="logo__link" to="/" aria-label="Haier">
                    <Logo />
                  </Link>
                </div>
                {legalInfo && (
                  <span className="d-none d-md-block ml-3 align-self-end footer__copyright">
                    {legalInfo}
                  </span>
                )}
              </div>
              {localeContent && (
                <div className="footer__language d-inline-flex align-items-center">
                  <img
                    className="footer__flag"
                    src={localeContent.image?.node?.resize?.src}
                    alt={localeContent.image?.value?.description || ""}
                  />
                  <a
                    href={withPrefix(generatedLocale) || localeContent.externalLink}
                    className="footer__link d-inline-flex align-items-center"
                    target={localeContent.target || "self"}
                    rel={localeContent.target === "_blank" ? "noopener noreferrer" : ""}
                  >
                    <span className="text-nowrap">{localeContent.liferayFields.title}</span>
                    <ArrowRight className="footer__arrow ml-2" />
                  </a>
                </div>
              )}
            </div>
          </div>
          <div
            className={classNames(
              "col-lg-9",
              !!customFooter &&
                columnName?.length === 1 &&
                !payAndSocialFooter?.find(
                  (column) => column?.pagePayAndSocial && column?.columnPayAndSocial !== ""
                ) &&
                "col-lg-12"
            )}
          >
            <div className="row">
              {columnName?.map((item, i) => (
                <div
                  className={classNames(
                    "col-md-3",
                    !!customFooter && columnName?.length === 1 && "col-md-12"
                  )}
                  key={i}
                >
                  <div className={`footer__item${openMap[i] ? " open" : ""}`}>
                    <button className="d-md-none footer__label" name={i} onClick={toggleItem}>
                      {item.value}
                      <ArrowDown
                        className={classNames(
                          "footer__toggle",
                          !!customFooter && columnName?.length === 1 && "d-none"
                        )}
                      />
                    </button>
                    <span
                      className={classNames(
                        !(
                          !!customFooter &&
                          columnName?.length === 1 &&
                          !payAndSocialFooter?.find(
                            (column) =>
                              column?.pagePayAndSocial && column?.columnPayAndSocial !== ""
                          )
                        ) && "d-none d-md-block footer__label",
                        !!customFooter &&
                          columnName?.length === 1 &&
                          !payAndSocialFooter?.find(
                            (column) =>
                              column?.pagePayAndSocial && column?.columnPayAndSocial !== ""
                          ) &&
                          "d-none"
                      )}
                    >
                      {item.columnName}
                    </span>
                    <ul
                      className={classNames(
                        "footer__list",
                        !!customFooter &&
                          columnName?.length === 1 &&
                          payAndSocialFooter?.find(
                            (column) =>
                              column?.pagePayAndSocial && column?.columnPayAndSocial !== ""
                          ) &&
                          "footer__list--single d-md-flex justify-content-md-left d-block",
                        !!customFooter &&
                          columnName?.length === 1 &&
                          !payAndSocialFooter?.find(
                            (column) =>
                              column?.pagePayAndSocial && column?.columnPayAndSocial !== ""
                          ) &&
                          "footer__list--single d-md-flex justify-content-md-center d-block"
                      )}
                    >
                      <Pages item={item.content.page} region={region} />
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-lg-3">
            <div className="row">
              {payAndSocialFooter &&
                payAndSocialFooter
                  .filter((item) => item.columnPayAndSocial && item.columnPayAndSocial !== "null")
                  .map((item, i) => (
                    <div className="col-md-6 col-lg-12" key={i}>
                      <div className="footer__item">
                        <div className="footer__label">{item.value}</div>
                        <div className="pb-3 d-flex flex-wrap">
                          {item.pagePayAndSocial?.map((elem, i) =>
                            elem?.alternativeIcon?.alternativeIcon?.node?.publicURL &&
                            elem?.externalLink ? (
                              <Link
                                target={elem?.target?.[0]}
                                to={elem?.externalLink}
                                className="footer__link footer__link-alternative-icon"
                                key={i}
                              >
                                <img
                                  className="footer__social-alternative-icon"
                                  src={elem.alternativeIcon.alternativeIcon.node.publicURL}
                                  alt={elem?.alternativeIcon?.alternativeIcon?.value?.description}
                                  style={{
                                    width: elem?.alternativeIcon?.width
                                      ? `${elem?.alternativeIcon?.width}px`
                                      : null,
                                    height: elem?.alternativeIcon?.height
                                      ? `${elem?.alternativeIcon?.height}px`
                                      : null,
                                  }}
                                />
                              </Link>
                            ) : elem?.alternativeIcon?.alternativeIcon?.node?.publicURL ? (
                              <span
                                className="footer__link footer__link-alternative-icon footer__no-link-alternative-icon"
                                key={i}
                              >
                                <img
                                  className="footer__social-alternative-icon"
                                  src={elem.alternativeIcon.alternativeIcon.node.publicURL}
                                  alt={elem?.alternativeIcon?.alternativeIcon?.value?.description}
                                  style={{
                                    width: elem?.alternativeIcon?.width
                                      ? `${elem?.alternativeIcon?.width}px`
                                      : null,
                                    height: elem?.alternativeIcon?.height
                                      ? `${elem?.alternativeIcon?.height}px`
                                      : null,
                                  }}
                                />
                              </span>
                            ) : (
                              <FooterIcon
                                item={elem}
                                /* className={i > 0 ? 'ml-4' : ''} */ key={i}
                              />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
            </div>
          </div>
          {legalInfo && (
            <div className="col-12 d-md-none">
              <div className="footer__item py-3">
                <span className="footer__copyright">{legalInfo}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Footer;
export const query = graphql`
  {
    footerMenuFiltered {
      columnNameFieldSet {
        columnName
        page {
          ... on LiferayPage {
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageLine {
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageCrossLine {
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageExternalLink {
            liferayFields {
              title
            }
            externalLink
            target
          }
        }
      }
      payAndSocialFooter {
        columnPayAndSocial
        pagePayAndSocial {
          ... on LiferayPageExternalLink {
            liferayFields {
              title
            }
            icon
            externalLink
            target
            alternativeIcon {
              alternativeIcon {
                value {
                  description
                }
                node {
                  publicURL
                }
              }
              height
              width
            }
          }
        }
      }
      legalInfo
      localeContent {
        ... on LiferayPageExternalLink {
          liferayFields {
            title
          }
          icon
          image {
            node {
              resize(width: 20, quality: 100) {
                src
              }
            }
            value {
              description
            }
          }
          externalLink
          target
        }
      }
    }
  }
`;
